// Service functions for the foreign PEP / sanctions report review flags.
//
// These hit the same-origin DueDiligence controller (cookie/session auth, same as
// GetKYCAnswers / SetRiskscore). The two endpoints are independent: SetPepFalsePositive
// only touches the flag, SetPepComment only touches the comment.
//
// On failure the API returns a non-2xx status with the (Icelandic) error message as a
// plain string body, which is safe to surface to the user.

export interface SetPepFalsePositiveRequest {
    pepReportId: number;
    falsePositive: boolean;
}

export interface SetPepCommentRequest {
    pepReportId: number;
    comment: string | null;
}

/**
 * Sends a PATCH to /DueDiligence/{action} and returns the echoed request body on success.
 * Throws an Error whose message is the server's plain-text error body (falling back to the
 * HTTP status) on any non-2xx response.
 */
async function patch<T>(action: string, body: T): Promise<T> {
    const res = await fetch(`/DueDiligence/${action}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const message = await res.text();
        throw new Error(message || `Beiðni mistókst (${res.status})`);
    }
    return res.json();
}

/** Toggle the whole-report false-positive flag. Does not touch the comment. */
export const setPepFalsePositive = (pepReportId: number, falsePositive: boolean) =>
    patch<SetPepFalsePositiveRequest>("SetPepFalsePositive", { pepReportId, falsePositive });

/** Set / edit / clear the reviewer comment. Send null or "" to clear. Does not touch the flag. */
export const setPepComment = (pepReportId: number, comment: string | null) =>
    patch<SetPepCommentRequest>("SetPepComment", { pepReportId, comment });

/** Toggle the reputational-risk (Orðsporsáhætta) false-positive flag. Independent of setPepFalsePositive. */
export const setPepReputationalFalsePositive = (pepReportId: number, falsePositive: boolean) =>
    patch<SetPepFalsePositiveRequest>("SetPepReputationalFalsePositive", { pepReportId, falsePositive });

/** Set / edit / clear the reputational-risk reviewer comment. Independent of setPepComment. */
export const setPepReputationalComment = (pepReportId: number, comment: string | null) =>
    patch<SetPepCommentRequest>("SetPepReputationalComment", { pepReportId, comment });
